/**
 * PR-E5 — runtime font resolver for the Three.js renderer.
 *
 * Given a logical font name (DXF `STYLE.fontName` or MText `\f` token),
 * returns either an SDF atlas resource or an SHX stroke resource so
 * the renderer can dispatch glyph drawing to the right pipeline.
 *
 * # Detection rule
 *
 * Mirrors AutoCAD's STYLE-table logic:
 *
 *   1. Strip ".shx" / ".SHX" suffix.
 *   2. If the lowercase root matches a known SHX family token
 *      (ROMANS, SIMPLEX, ISOCPEUR, ISOCP, ITALIC, ROMAND, ROMANC,
 *      ROMANT, COMPLEX, GOTHICE, GOTHICG, GOTHICI, MONOTXT, TXT,
 *      ARIAL — yes, AutoCAD ships an "Arial" SHX too), the font is
 *      treated as SHX. The same name without the suffix is also
 *      treated as SHX so style-table strings like `"romans"` resolve
 *      identically.
 *   3. If the original input ended in `.shx` (case-insensitive), the
 *      font is SHX regardless of the root name. This is the most
 *      reliable signal — DXF rarely contains a `.shx` suffix without
 *      meaning it.
 *   4. Otherwise the font is treated as TTF / SDF and the SDF atlas
 *      pipeline is used.
 *
 * # Trade-offs vs `apps/web/src/app/fonts/fontRegistry.ts`
 *
 * That registry is the source of truth for font BYTES (SHX raw + TTF
 * FontFace). This module is a thin runtime resolver on top of it that
 * speaks the `SdfAtlas` / `ShxStrokeFont` languages of the new scene3d
 * pipeline. We keep them separate so:
 *   - the legacy SVG renderer keeps using the byte-level FontRegistry,
 *   - this module can apply scene3d-specific caching (one promise per
 *     font name, mirroring `loadSdfAtlas`),
 *   - both paths can run side-by-side during the migration window.
 *
 * # Q15.C — graceful fallback
 *
 * `resolveFont` rejects with `MissingFontError` if neither path can
 * produce a usable resource. The Scene3DStage caller catches the
 * rejection and emits the "?????" placeholder rectangle path that
 * already exists for SDF.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity
 *
 * No simplification of the SHX detection rule: the suffix-stripping
 * mirror of `apps/web/src/app/fonts/fontStore.ts:normalizeFontKey`
 * preserves every ambiguous case rather than guessing.
 */

import {
  loadSdfAtlas,
  MissingAtlasError,
  type SdfAtlas,
} from "./sdfAtlasLoader";
import {
  loadShxFontAsLineGroups,
  MissingShxFontError,
  type ShxStrokeFont,
} from "./shxStrokeAtlas";
import { mapCisFontToAtlas, DEFAULT_SDF_FONT } from "./cisFontAtlas";

// ── Public types ─────────────────────────────────────────────────

/**
 * Discriminated font resource returned by `resolveFont`. Consumers
 * dispatch on `kind`:
 *
 *   - `"sdf"` → instanced glyph quads via the SDF text material.
 *   - `"shx"` → straight-line segments via `THREE.LineSegments`.
 */
export type FontResource =
  | { kind: "sdf"; atlas: SdfAtlas }
  | { kind: "shx"; strokes: ShxStrokeFont };

/**
 * Raised when neither the SDF nor the SHX backing store can produce a
 * font resource for the requested name. The original error from the
 * underlying loader is attached as `cause` so devtools can surface
 * which path failed first.
 */
export class MissingFontError extends Error {
  readonly fontName: string;
  readonly sdfError?: unknown;
  readonly shxError?: unknown;
  constructor(
    fontName: string,
    message: string,
    options: { sdfError?: unknown; shxError?: unknown } = {},
  ) {
    super(`[scene3d.fontRegistry] ${fontName}: ${message}`);
    this.name = "MissingFontError";
    this.fontName = fontName;
    if (options.sdfError !== undefined) this.sdfError = options.sdfError;
    if (options.shxError !== undefined) this.shxError = options.shxError;
  }
}

// ── SHX detection (parity with AutoCAD STYLE table) ──────────────

/**
 * Lowercase SHX family tokens AutoCAD's STYLE table treats as native
 * stroke fonts. Maintained as a `Set` so detection is O(1). Add new
 * names here when the PR-E plan ships additional SHX defaults.
 */
const KNOWN_SHX_FAMILIES = new Set<string>([
  "romans",
  "romand",
  "romanc",
  "romant",
  "simplex",
  "complex",
  "italic",
  "italicc",
  "italict",
  "isocpeur",
  "isocp",
  "isocp2",
  "isocp3",
  "isoct",
  "isoct2",
  "isoct3",
  "isocp_eur",
  "monotxt",
  "txt",
  "gothice",
  "gothicg",
  "gothici",
  "scripts",
  "scriptc",
]);

/**
 * Check whether `fontName` denotes an SHX stroke font.
 *
 * Detection rule:
 *   - Trailing `.shx` (any case) → SHX.
 *   - Lowercase root matches a `KNOWN_SHX_FAMILIES` token → SHX.
 *   - Otherwise → not SHX (assume TTF / SDF).
 *
 * Exposed (not just internal) so the FontManagerDialog can mirror the
 * same rule when classifying user uploads, avoiding rule drift.
 */
export function isShxFontName(fontName: string): boolean {
  const trimmed = fontName.trim();
  if (trimmed === "") return false;
  if (/\.shx$/iu.test(trimmed)) return true;
  const root = trimmed.toLowerCase().replace(/\.shx$/u, "");
  return KNOWN_SHX_FAMILIES.has(root);
}

// ── Public API ───────────────────────────────────────────────────

/**
 * Resolve `fontName` to a renderer-ready `FontResource`.
 *
 * Routing:
 *   - `isShxFontName(fontName) === true` → try SHX first via
 *     `loadShxFontAsLineGroups`. On `MissingShxFontError`, fall back
 *     to SDF (some users register a TTF override under the same logical
 *     name); if that also fails, reject with `MissingFontError`.
 *   - `isShxFontName(fontName) === false` → SDF first; on
 *     `MissingAtlasError`, do not fall back to SHX (TTF families have
 *     no automatic SHX equivalent).
 *
 * The Scene3DStage caller catches `MissingFontError` and switches to
 * the "?????" placeholder path so the user still sees text where the
 * entity should render.
 */
export async function resolveFont(fontName: string): Promise<FontResource> {
  const requested = fontName.trim();
  if (requested === "") {
    throw new MissingFontError(requested, "empty font name");
  }

  if (isShxFontName(requested)) {
    try {
      const strokes = await loadShxFontAsLineGroups(requested);
      return { kind: "shx", strokes };
    } catch (shxError) {
      // @codex #334 — CIS atlas fallback: a REGISTERED SHX always wins
      // (it was tried first above), but an UNREGISTERED CIS .shx
      // (`gost.shx`, `isocpeur`) would otherwise 404 on the
      // `loadSdfAtlas(requested)` below (no atlas is shipped under
      // "gost.shx") and reach the "?????" placeholder. Map it to the
      // bundled OpenGost atlas (real Cyrillic glyphs) first. NON-CIS SHX
      // names (`romant.shx`) return `null` here and fall through to the
      // same-name SDF override path unchanged.
      const cisAtlas = mapCisFontToAtlas(requested);
      if (cisAtlas) {
        // @codex #334 — mirror the same-name / non-SHX paths' error
        // contract: if the bundled OpenGost atlas itself fails to load
        // (transient fetch error / atlas removed during a deploy
        // window), convert `MissingAtlasError` → `MissingFontError` so
        // the caller's catch still renders the "?????" placeholder.
        // A bare throw here would escape as a raw `MissingAtlasError`,
        // hit the caller's `else` branch, and silently render NOTHING.
        try {
          const atlas = await loadSdfAtlas(cisAtlas);
          return { kind: "sdf", atlas };
        } catch (cisAtlasError) {
          if (cisAtlasError instanceof MissingAtlasError) {
            throw new MissingFontError(
              requested,
              "CIS atlas substitute failed to load",
              { sdfError: cisAtlasError, shxError },
            );
          }
          // Re-throw unexpected errors verbatim so devtools surface the
          // actual stack trace instead of swallowing it.
          throw cisAtlasError;
        }
      }
      // SHX-by-name can still resolve to a TTF override if the user
      // registered one — try the SDF path before giving up. We use
      // the same logical name; the SDF loader will 404 if no atlas is
      // shipped under that name.
      try {
        const atlas = await loadSdfAtlas(requested);
        return { kind: "sdf", atlas };
      } catch (sdfError) {
        if (!(sdfError instanceof MissingAtlasError)) {
          // Re-throw unexpected errors verbatim so devtools surface the
          // actual stack trace instead of swallowing it.
          throw sdfError;
        }
        // @codex #334 — readable last resort for an UNBUNDLED SHX. Once the
        // STYLE→fontName plumbing resolves styles to their real (often .shx)
        // font file, AutoCAD defaults like `txt` / `romans.shx` (no bundled
        // atlas, not a CIS family) would otherwise hit the "?????"
        // placeholder — turning previously-readable text into boxes. Fall
        // back to the Cyrillic-capable default SDF (Roboto), matching the
        // non-SHX path's Roboto fallback. Only if the default atlas ITSELF
        // is missing do we surface MissingFontError → placeholder.
        try {
          const atlas = await loadSdfAtlas(DEFAULT_SDF_FONT);
          return { kind: "sdf", atlas };
        } catch {
          throw new MissingFontError(
            requested,
            "no SHX font, no same-name SDF atlas, default SDF unavailable",
            { sdfError, shxError },
          );
        }
      }
    }
  }

  // Non-SHX path: try SDF only. We deliberately do NOT fall back to
  // SHX here — TTF fonts that fail to load shouldn't suddenly render
  // as a stroke font with completely different metrics; that would
  // cause harder-to-debug visual regressions than a missing-atlas
  // placeholder.
  try {
    const atlas = await loadSdfAtlas(requested);
    return { kind: "sdf", atlas };
  } catch (sdfError) {
    if (sdfError instanceof MissingAtlasError) {
      throw new MissingFontError(
        requested,
        "no SDF atlas shipped for non-SHX font",
        { sdfError },
      );
    }
    throw sdfError;
  }
}

/**
 * Re-export the underlying error classes so the Scene3DStage's catch
 * block can pattern-match without importing from three different
 * modules.
 */
export { MissingAtlasError, MissingShxFontError };
